import { useCallback, useEffect, useRef, useState } from "react"
import "./index.css"

import { nes } from "./lib/emulator"
import { Cartridge } from "./lib/emulator/Cartridge"

type Key = "up" | "down" | "left" | "right" | "a" | "b" | "select" | "start"

const KEYBOARD_MAPPING: Record<string, Key> = {
    x: "a",
    z: "b",
    Control: "select",
    Enter: "start",
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
    w: "up",
    s: "down",
    a: "left",
    d: "right",
}

export function App() {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const keysHeld = useRef<Set<Key>>(new Set())

    const updateController = useCallback(() => {
        nes.controller[0] = 0x00
        nes.controller[1] = 0x00

        nes.controller[0] |= keysHeld.current.has("a") ? 0x80 : 0x00
        nes.controller[0] |= keysHeld.current.has("b") ? 0x40 : 0x00
        nes.controller[0] |= keysHeld.current.has("select") ? 0x20 : 0x00
        nes.controller[0] |= keysHeld.current.has("start") ? 0x10 : 0x00
        nes.controller[0] |= keysHeld.current.has("up") ? 0x08 : 0x00
        nes.controller[0] |= keysHeld.current.has("down") ? 0x04 : 0x00
        nes.controller[0] |= keysHeld.current.has("left") ? 0x02 : 0x00
        nes.controller[0] |= keysHeld.current.has("right") ? 0x01 : 0x00

        const gamepads = navigator.getGamepads()
        for (let i = 0; i < 2; i++) {
            const gamepad = gamepads[i]
            if (!gamepad) continue

            nes.controller[i]! |= gamepad.buttons[1]?.pressed ? 0x80 : 0x00
            nes.controller[i]! |= gamepad.buttons[0]?.pressed ? 0x40 : 0x00
            nes.controller[i]! |= gamepad.buttons[8]?.pressed ? 0x20 : 0x00
            nes.controller[i]! |= gamepad.buttons[9]?.pressed ? 0x10 : 0x00
            nes.controller[i]! |= gamepad.axes[1] === -1 ? 0x08 : 0x00
            nes.controller[i]! |= gamepad.axes[1] === 1 ? 0x04 : 0x00
            nes.controller[i]! |= gamepad.axes[0] === -1 ? 0x02 : 0x00
            nes.controller[i]! |= gamepad.axes[0] === 1 ? 0x01 : 0x00
        }
    }, [])

    const handleDrop = async (event: React.DragEvent) => {
        event.preventDefault()
        const file = event.dataTransfer.files[0]
        if (!file) return

        const arrayBuffer = await file.arrayBuffer()
        const romData = new Uint8Array(arrayBuffer)
        const cartridge = new Cartridge(romData)
        nes.insertCartridge(cartridge)
        nes.reset()
    }

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        canvas.width = nes.ppu.SCREEN_WIDTH
        canvas.height = nes.ppu.SCREEN_HEIGHT

        const imageData = ctx.createImageData(256, 240)

        let animationFrameId: number
        const FRAME_TIME = 1000 / 60 - 1
        let lastTime = 0

        const loop = (timestamp: number) => {
            animationFrameId = requestAnimationFrame(loop)

            if (timestamp - lastTime >= FRAME_TIME) {
                lastTime = timestamp - ((timestamp - lastTime) % FRAME_TIME)

                updateController()

                do {
                    nes.clock()
                } while (!nes.ppu.frameComplete)
                nes.ppu.frameComplete = false

                imageData.data.set(nes.ppu.frameBuffer)
                ctx.putImageData(imageData, 0, 0)
            }
        }

        animationFrameId = requestAnimationFrame(loop)

        const handleKeyDown = (e: KeyboardEvent) => {
            const controllerKey = KEYBOARD_MAPPING[e.key]
            if (!controllerKey) return

            e.preventDefault()
            keysHeld.current.add(controllerKey)
        }

        const handleKeyUp = ({ key }: KeyboardEvent) => {
            const controllerKey = KEYBOARD_MAPPING[key]
            if (controllerKey) keysHeld.current.delete(controllerKey)
        }

        document.addEventListener("keydown", handleKeyDown)
        document.addEventListener("keyup", handleKeyUp)

        return () => {
            document.removeEventListener("keydown", handleKeyDown)
            document.removeEventListener("keyup", handleKeyUp)
            cancelAnimationFrame(animationFrameId)
        }
    }, [])

    const loadFile = async (name: string) => {
        const file = await fetch(`/downloads/${name}`)
        const romData = await file.bytes()
        const cartridge = new Cartridge(romData)
        nes.insertCartridge(cartridge)
        nes.reset()
    }

    const [dragging, setDragging] = useState(false)

    return (
        <div
            className="app"
            onDrop={(e) => {
                setDragging(false)
                handleDrop(e)
            }}
            onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
        >
            {/* Floating panel */}
            <div className="panel">
                <h1>NES Emulator</h1>

                <div className="panel-section">
                    <span className="panel-label">Games</span>
                    <button onClick={() => loadFile("mario.nes")}>Mario</button>
                    <button onClick={() => loadFile("donkey-kong.nes")}>Donkey Kong</button>
                    <button onClick={() => loadFile("tetris.nes")}>Tetris</button>
                    <button onClick={() => loadFile("race.nes")}>Race</button>
                    {/* <button onClick={() => loadFile("nestest.nes")}>NES Test</button> */}
                </div>

                <div className="panel-section">
                    <button className="btn-reset" onClick={() => nes.reset()}>
                        Reset
                    </button>
                </div>

                <div className="key-hints">
                    <span className="panel-label">Controls</span>
                    <span>
                        <kbd>WASD</kbd> <span className="hint-or">/</span> <kbd>Arrows</kbd> D-Pad
                    </span>
                    <span>
                        <kbd>Z</kbd> B
                    </span>
                    <span>
                        <kbd>X</kbd> A
                    </span>
                    <span>
                        <kbd>Ctrl</kbd> Select
                    </span>
                    <span>
                        <kbd>Enter</kbd> Start
                    </span>
                </div>

                <div className="panel-footer">
                    <p>Drop a .nes file to load</p>
                    <p>
                        Gamepad supported — just connect a controller and it will be detected automatically.
                    </p>
                </div>
            </div>

            {/* Canvas */}
            <div className="canvas-area">
                <div className={`canvas-wrapper${dragging ? " dragging" : ""}`}>
                    <canvas ref={canvasRef} className="nes-canvas" />
                    <div className="drop-overlay">Drop ROM file here</div>
                </div>
            </div>
        </div>
    )
}

export default App
